import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createBrokerConnection,
  deleteBrokerConnection,
  exchangeUpstoxToken,
  getBrokerSettingsSchema,
  exchangeZerodhaToken,
  listBrokerConnections,
  refreshUpstoxTokenForConnection,
  refreshZerodhaTokenForConnection,
  snapshotInstruments,
  testBrokerConnection,
  updateBrokerConnection,
  REQUIRED_CREDENTIAL_FIELDS,
  upstoxLoginUrl,
  zerodhaLoginUrl,
  type BrokerConnection,
  type BrokerHealthResult,
  type BrokerMarginLine,
  type BrokerKind,
  type BrokerSettingSpec,
} from "../api/brokers";
import Card from "../components/Card";
import ChargesVerifier from "../components/ChargesVerifier";
import Button from "../components/Button";
import { ApiError } from "../lib/api";
import { getMyPolicy, MY_POLICY_KEY } from "../api/policy";

export const BROKER_SETTINGS_SCHEMA_KEY = ["broker-settings-schema"] as const;

/** Server-described settings, so bounds and help text can never drift from the
 *  validator that enforces them. Long-lived: the schema is code, not data. */
function useBrokerSettingsSchema() {
  return useQuery({
    queryKey: BROKER_SETTINGS_SCHEMA_KEY,
    queryFn: getBrokerSettingsSchema,
    staleTime: 60 * 60 * 1000,
  });
}

/** Renders one broker's editable non-secret settings.
 *
 *  Values are held as STRINGS while typing — a number-typed state would make
 *  clearing the box impossible (an empty input is not a number), and blank is
 *  a meaningful choice here: it means "use the default".
 */
function BrokerSettingsFields({
  specs,
  values,
  onChange,
}: {
  specs: BrokerSettingSpec[] | undefined;
  values: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
}) {
  if (!specs || specs.length === 0) return null;
  return (
    <div className="space-y-3">
      {specs.map((s) => (
        <div key={s.key}>
          <label className="block text-sm font-medium text-fg-subtle mb-1">
            {s.label}
          </label>
          <input
            type="number"
            step="0.1"
            min={s.minimum}
            max={s.maximum}
            value={values[s.key] ?? ""}
            onChange={(e) => onChange({ ...values, [s.key]: e.target.value })}
            placeholder={`default ${s.default}`}
            className="w-full border border-border rounded px-3 py-2"
          />
          <p className="text-xs text-muted mt-1">
            {s.help} Leave blank to use the default ({s.default}). Allowed:{" "}
            {s.minimum}–{s.maximum}.
          </p>
        </div>
      ))}
    </div>
  );
}

/** Blank string -> omitted (use the default); anything else -> a number.
 *  Non-numeric text is dropped rather than sent, so the server's 400 is
 *  reserved for genuinely out-of-range values. */
function toSettingsPayload(
  values: Record<string, string>,
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(values)) {
    if (v.trim() === "") continue;
    const n = Number(v);
    if (Number.isFinite(n)) out[k] = n;
  }
  return out;
}

const BROKER_LABELS: Record<BrokerKind, string> = {
  delta: "Delta Exchange India",
  zerodha: "Zerodha (Kite Connect)",
  dhan: "Dhan",
  upstox: "Upstox (data only)",
};

export default function BrokersPage() {
  const qc = useQueryClient();
  const { data: conns, isLoading } = useQuery({
    queryKey: ["broker-connections"],
    queryFn: listBrokerConnections,
  });
  const del = useMutation({
    mutationFn: deleteBrokerConnection,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["broker-connections"] });
      // "X of Y accounts used" is derived server-side; refresh it too.
      void qc.invalidateQueries({ queryKey: MY_POLICY_KEY });
    },
  });
  const [healthByConn, setHealthByConn] = useState<Record<string, BrokerHealthResult>>({});
  const [testingId, setTestingId] = useState<string | null>(null);

  async function runTest(id: string) {
    setTestingId(id);
    try {
      const result = await testBrokerConnection(id);
      setHealthByConn((prev) => ({ ...prev, [id]: result }));
    } catch (e) {
      setHealthByConn((prev) => ({
        ...prev,
        [id]: {
          status: "error",
          message:
            e instanceof ApiError
              ? `Request failed: ${String(e.detail ?? e.message)}`
              : "Request failed (network error)",
        },
      }));
    } finally {
      setTestingId(null);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl sm:text-2xl font-semibold">Broker connections</h1>
      </div>

      <Card title="Add a new connection">
        <NewConnectionForm
          onSuccess={() => {
            void qc.invalidateQueries({ queryKey: ["broker-connections"] });
            void qc.invalidateQueries({ queryKey: MY_POLICY_KEY });
          }}
        />
      </Card>

      <Card title="Your connections">
        {isLoading && <div className="text-sm text-muted">Loading…</div>}
        {conns && conns.length === 0 && (
          <div className="text-sm text-muted">No connections yet.</div>
        )}
        {conns && conns.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-muted border-b border-border/60">
                <tr>
                  <th className="py-2 pr-3">Broker</th>
                  <th className="py-2 pr-3">Label</th>
                  <th className="py-2 pr-3">Credential fields</th>
                  <th className="py-2 pr-3">Created</th>
                  <th className="py-2 pr-3">Health</th>
                  <th className="py-2"></th>
                </tr>
              </thead>
              <tbody>
                {conns.map((c) => (
                  <ConnectionRow
                    key={c.id}
                    conn={c}
                    health={healthByConn[c.id]}
                    isTesting={testingId === c.id}
                    isDeleting={del.isPending}
                    onTest={() => runTest(c.id)}
                    onDelete={() => {
                      if (confirm(`Delete ${c.broker}/${c.label}?`)) del.mutate(c.id);
                    }}
                    onRefreshed={() => {
                      void qc.invalidateQueries({ queryKey: ["broker-connections"] });
                      void qc.invalidateQueries({ queryKey: MY_POLICY_KEY });
                      runTest(c.id);
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function ConnectionRow({
  conn,
  health,
  isTesting,
  isDeleting,
  onTest,
  onDelete,
  onRefreshed,
}: {
  conn: BrokerConnection;
  health: BrokerHealthResult | undefined;
  isTesting: boolean;
  isDeleting: boolean;
  onTest: () => void;
  onDelete: () => void;
  onRefreshed: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [snapping, setSnapping] = useState(false);
  const [snapMsg, setSnapMsg] = useState<{ ok: boolean; text: string } | null>(null);

  async function refreshInstruments() {
    setSnapping(true);
    setSnapMsg(null);
    try {
      const r = await snapshotInstruments(conn.id);
      setSnapMsg({
        ok: true,
        text: `Instruments refreshed: ${r.rows.toLocaleString()} contracts (snapshot ${r.snapshot_date}).`,
      });
    } catch (e) {
      setSnapMsg({
        ok: false,
        text:
          e instanceof ApiError
            ? `Refresh failed: ${String(e.detail ?? e.message)}`
            : "Refresh failed (network error)",
      });
    } finally {
      setSnapping(false);
    }
  }

  return (
    <>
      <tr className="border-b border-border/60">
        <td className="py-2 pr-3 font-mono">{conn.broker}</td>
        <td className="py-2 pr-3">{conn.label}</td>
        <td className="py-2 pr-3 font-mono text-xs text-muted">
          {conn.credential_keys.join(", ")}
        </td>
        <td className="py-2 pr-3 text-muted text-xs">
          {new Date(conn.created_at).toLocaleString()}
        </td>
        <td className="py-2 pr-3">
          <HealthBadge result={health} />
        </td>
        <td className="py-2 text-right space-x-2 whitespace-nowrap">
          <Button variant="secondary" size="sm" disabled={isTesting} onClick={onTest}>
            {isTesting ? "Testing…" : "Test"}
          </Button>
          {conn.broker === "zerodha" && (
            <Button
              variant="secondary"
              size="sm"
              disabled={snapping}
              onClick={refreshInstruments}
              title="Download today's option chains / expiries / tokens into the local store"
            >
              {snapping ? "Refreshing…" : "Refresh instruments"}
            </Button>
          )}
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setEditing((v) => !v)}
          >
            {editing ? "Cancel" : "Edit credentials"}
          </Button>
          <Button variant="danger" size="sm" disabled={isDeleting} onClick={onDelete}>
            Delete
          </Button>
        </td>
      </tr>
      {snapMsg && (
        <tr className="border-b border-border/60 last:border-0">
          <td
            colSpan={6}
            className={`py-2 pr-3 text-xs ${snapMsg.ok ? "text-up-600 dark:text-up-400" : "text-down-600 dark:text-down-400"}`}
          >
            {snapMsg.text}
          </td>
        </tr>
      )}
      {(health || editing) && (
        <tr className="border-b border-border/60 last:border-0">
          <td colSpan={6} className="py-2 pr-3 space-y-2">
            {health && (
              <HealthDetails conn={conn} result={health} onRefreshed={onRefreshed} />
            )}
            {editing && (
              <EditCredentialsForm
                conn={conn}
                onDone={() => {
                  setEditing(false);
                  onRefreshed();
                }}
              />
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function EditCredentialsForm({
  conn,
  onDone,
}: {
  conn: BrokerConnection;
  onDone: () => void;
}) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const settingsSchema = useBrokerSettingsSchema();
  const specs = settingsSchema.data?.[conn.broker];
  // Seeded from what is stored, because settings PATCH replaces the whole bag:
  // submitting only the field the user touched would silently clear the others.
  const [settingsValues, setSettingsValues] = useState<Record<string, string>>(
    () =>
      Object.fromEntries(
        Object.entries(conn.settings ?? {}).map(([k, v]) => [k, String(v)]),
      ),
  );

  const fields = REQUIRED_CREDENTIAL_FIELDS[conn.broker];

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const credentials: Record<string, string> = {};
    for (const [k, v] of Object.entries(values)) {
      if (v.trim() !== "") credentials[k] = v.trim();
    }
    const settings = toSettingsPayload(settingsValues);
    const settingsChanged =
      JSON.stringify(settings) !== JSON.stringify(conn.settings ?? {});
    if (Object.keys(credentials).length === 0 && !settingsChanged) {
      setErr("Change a credential or a setting to update.");
      return;
    }
    setBusy(true);
    setErr(null);
    setInfo(null);
    try {
      await updateBrokerConnection(conn.id, {
        // Credentials are merged server-side; omit them entirely when
        // untouched so a settings-only edit doesn't re-run the token check.
        ...(Object.keys(credentials).length > 0 ? { credentials } : {}),
        ...(settingsChanged ? { settings } : {}),
      });
      setInfo(
        Object.keys(credentials).length > 0
          ? "✓ Connection updated."
          : "✓ Settings updated.",
      );
      setValues({});
      onDone();
    } catch (e) {
      setErr(
        e instanceof ApiError
          ? `Update failed: ${String(e.detail ?? e.message)}`
          : "Update failed (network error)",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={submit}
      className="bg-surface-2 border border-border rounded p-3 space-y-2 text-xs"
    >
      <div className="text-fg-subtle font-medium">
        Update {conn.broker}/{conn.label}
      </div>
      <div className="text-muted">
        Fill only the fields you want to change. Blank fields keep their current
        value. Existing values are not shown for security.
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {fields.map((f) => (
          <div key={f}>
            <label className="block text-fg-subtle mb-1 font-mono">{f}</label>
            <input
              type="password"
              value={values[f] ?? ""}
              onChange={(e) => setValues({ ...values, [f]: e.target.value })}
              placeholder="leave blank to keep current"
              className="w-full border border-border rounded px-2 py-1 font-mono"
              autoComplete="off"
            />
          </div>
        ))}
      </div>
      {specs && specs.length > 0 && (
        <div className="pt-2 border-t border-border/60">
          <BrokerSettingsFields
            specs={specs}
            values={settingsValues}
            onChange={setSettingsValues}
          />
        </div>
      )}
      <div className="pt-2 border-t border-border/60">
        <ChargesVerifier broker={conn.broker} />
      </div>
      {err && <div className="text-down-700 dark:text-down-400">{err}</div>}
      {info && <div className="text-up-700 dark:text-up-400">{info}</div>}
      <div>
        <Button type="submit" size="sm" disabled={busy}>
          {busy ? "Saving…" : "Save"}
        </Button>
      </div>
    </form>
  );
}

function HealthBadge({ result }: { result: BrokerHealthResult | undefined }) {
  if (!result) return <span className="text-xs text-muted">not tested</span>;
  const cls = (() => {
    switch (result.status) {
      case "ok":
        return "bg-up-50 dark:bg-up-500/10 text-up-700 dark:text-up-400 border-up-200 dark:border-up-400/30";
      case "expired":
        return "bg-warn-50 dark:bg-warn-500/10 text-amber-700 dark:text-warn-400 border-warn-200 dark:border-warn-400/30";
      case "invalid":
        return "bg-down-50 dark:bg-down-500/10 text-down-700 dark:text-down-400 border-down-200 dark:border-down-400/30";
      case "missing_sdk":
        return "bg-surface-2 text-fg-subtle border-border";
      case "network_error":
        return "bg-orange-50 dark:bg-orange-500/10 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-400/30";
      default:
        return "bg-down-50 dark:bg-down-500/10 text-down-700 dark:text-down-400 border-down-200 dark:border-down-400/30";
    }
  })();
  return (
    <span className={`inline-block text-xs border rounded px-2 py-0.5 ${cls}`}>
      {result.status}
    </span>
  );
}

function HealthDetails({
  conn,
  result,
  onRefreshed,
}: {
  conn: BrokerConnection;
  result: BrokerHealthResult;
  onRefreshed: () => void;
}) {
  const bg =
    result.status === "ok"
      ? "bg-up-50 dark:bg-up-500/10 border-up-200 dark:border-up-400/30 text-up-700 dark:text-up-400"
      : result.status === "expired"
        ? "bg-warn-50 dark:bg-warn-500/10 border-warn-200 dark:border-warn-400/30 text-warn-700 dark:text-warn-400"
        : result.status === "network_error"
          ? "bg-orange-50 dark:bg-orange-500/10 border-orange-200 dark:border-orange-400/30 text-orange-800 dark:text-orange-200"
          : "bg-down-50 dark:bg-down-500/10 border-down-200 dark:border-down-400/30 text-down-700 dark:text-down-400";
  return (
    <div className={`text-xs border rounded px-3 py-2 ${bg} space-y-2`}>
      <div>{result.message}</div>
      {result.status === "ok" && <MarginLine result={result} />}
      {result.remedy && (
        <RemedyActions conn={conn} remedy={result.remedy} onRefreshed={onRefreshed} />
      )}
    </div>
  );
}

function fmtMargin(m: BrokerMarginLine): string {
  const n = Number(m.amount);
  if (!Number.isFinite(n)) return `${m.amount} ${m.currency}`;
  if (m.currency === "INR") {
    return `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  return `${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${m.currency}`;
}

function MarginLine({ result }: { result: BrokerHealthResult }) {
  const lines = result.margin ?? [];
  if (lines.length > 0) {
    return (
      <div className="text-sm text-fg">
        Available margin:{" "}
        <strong className="text-base font-bold">
          {lines.map((m) => fmtMargin(m)).join("  ·  ")}
        </strong>
      </div>
    );
  }
  if (result.margin_error) {
    return <div className="text-muted">Available margin: unavailable — {result.margin_error}</div>;
  }
  return null;
}

function RemedyActions({
  conn,
  remedy,
  onRefreshed,
}: {
  conn: BrokerConnection;
  remedy: NonNullable<BrokerHealthResult["remedy"]>;
  onRefreshed: () => void;
}) {
  if (remedy.action === "refresh_zerodha_token") {
    return (
      <ZerodhaRefreshAction
        connId={conn.id}
        loginUrl={remedy.login_url ?? null}
        onRefreshed={onRefreshed}
      />
    );
  }
  if (remedy.action === "refresh_upstox_token") {
    return (
      <UpstoxRefreshAction
        connId={conn.id}
        loginUrl={remedy.login_url ?? null}
        onRefreshed={onRefreshed}
      />
    );
  }
  if (remedy.url) {
    return (
      <div className="space-y-1">
        {remedy.instructions && <div>{remedy.instructions}</div>}
        <a
          href={remedy.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block underline font-medium"
        >
          Open broker portal →
        </a>
      </div>
    );
  }
  return null;
}

function ZerodhaRefreshAction({
  connId,
  loginUrl,
  onRefreshed,
}: {
  connId: string;
  loginUrl: string | null;
  onRefreshed: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const popupRef = useRef<Window | null>(null);
  const processedTokenRef = useRef<string | null>(null);

  useEffect(() => {
    async function handler(event: MessageEvent) {
      if (event.origin !== window.location.origin) return;
      if (
        event.data?.type !== "zerodha-request-token" &&
        event.data?.type !== "zerodha-request-token-error"
      )
        return;
      try {
        popupRef.current?.close();
      } catch {
        // ignore
      }
      popupRef.current = null;
      if (event.data?.type === "zerodha-request-token-error") {
        setBusy(false);
        setErr(`Zerodha login error: ${event.data.error}`);
        return;
      }
      const requestToken = event.data.request_token as string;
      if (processedTokenRef.current === requestToken) return;
      processedTokenRef.current = requestToken;
      setInfo("Exchanging request_token for access_token…");
      try {
        await refreshZerodhaTokenForConnection(connId, requestToken);
        setInfo("✓ access_token refreshed.");
        setErr(null);
        onRefreshed();
      } catch (e) {
        setErr(
          e instanceof ApiError
            ? `Refresh failed: ${String(e.detail ?? e.message)}`
            : "Refresh failed (network error)",
        );
        setInfo(null);
      } finally {
        setBusy(false);
      }
    }
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [connId, onRefreshed]);

  function openLogin() {
    setErr(null);
    setInfo(null);
    if (!loginUrl) {
      setErr("Backend did not provide a login URL — check api_key is stored on this connection.");
      return;
    }
    const popup = window.open(loginUrl, "zerodha-refresh", "width=600,height=720");
    if (!popup) {
      setErr("Popup blocked. Allow popups for this site and try again.");
      return;
    }
    popupRef.current = popup;
    setBusy(true);
    setInfo("Browser popup opened. Log in with your Kite PIN/2FA…");
  }

  return (
    <div className="space-y-1">
      <Button type="button" variant="secondary" size="sm" disabled={busy} onClick={openLogin}>
        {busy ? "Waiting…" : "Refresh Zerodha token"}
      </Button>
      {info && <div className="text-up-700 dark:text-up-400">{info}</div>}
      {err && <div className="text-down-700 dark:text-down-400">{err}</div>}
    </div>
  );
}

function UpstoxRefreshAction({
  connId,
  loginUrl,
  onRefreshed,
}: {
  connId: string;
  loginUrl: string | null;
  onRefreshed: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const popupRef = useRef<Window | null>(null);
  const processedCodeRef = useRef<string | null>(null);

  useEffect(() => {
    async function handler(event: MessageEvent) {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== "upstox-code" && event.data?.type !== "upstox-code-error")
        return;
      try {
        popupRef.current?.close();
      } catch {
        // ignore
      }
      popupRef.current = null;
      if (event.data?.type === "upstox-code-error") {
        setBusy(false);
        setErr(`Upstox login error: ${event.data.error}`);
        return;
      }
      const code = event.data.code as string;
      if (processedCodeRef.current === code) return;
      processedCodeRef.current = code;
      setInfo("Exchanging authorization code for access_token…");
      try {
        await refreshUpstoxTokenForConnection(connId, code);
        setInfo("✓ access_token refreshed.");
        setErr(null);
        onRefreshed();
      } catch (e) {
        setErr(
          e instanceof ApiError
            ? `Refresh failed: ${String(e.detail ?? e.message)}`
            : "Refresh failed (network error)",
        );
        setInfo(null);
      } finally {
        setBusy(false);
      }
    }
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [connId, onRefreshed]);

  function openLogin() {
    setErr(null);
    setInfo(null);
    if (!loginUrl) {
      setErr("Backend did not provide a login URL — check api_key + redirect_uri are stored.");
      return;
    }
    const popup = window.open(loginUrl, "upstox-refresh", "width=600,height=720");
    if (!popup) {
      setErr("Popup blocked. Allow popups for this site and try again.");
      return;
    }
    popupRef.current = popup;
    setBusy(true);
    setInfo("Browser popup opened. Log in to Upstox…");
  }

  return (
    <div className="space-y-1">
      <Button type="button" variant="secondary" size="sm" disabled={busy} onClick={openLogin}>
        {busy ? "Waiting…" : "Refresh Upstox token"}
      </Button>
      {info && <div className="text-up-700 dark:text-up-400">{info}</div>}
      {err && <div className="text-down-700 dark:text-down-400">{err}</div>}
    </div>
  );
}

function NewConnectionForm({ onSuccess }: { onSuccess: () => void }) {
  // Broker choices are limited to what an admin has enabled for this user,
  // and each is capped. The server enforces both (policy.assert_can_add_
  // broker_connection); this just avoids offering an option that would 403.
  const { data: policy } = useQuery({
    queryKey: MY_POLICY_KEY,
    queryFn: getMyPolicy,
  });
  const allowed = (policy?.brokers ?? []).filter((b) => b.enabled);
  const allowedKey = allowed.map((b) => b.broker).join(",");
  const atCap = (b: BrokerKind) => {
    const row = policy?.brokers.find((x) => x.broker === b);
    return !!row && row.used >= row.max_accounts;
  };
  const [broker, setBroker] = useState<BrokerKind>("delta");
  const [label, setLabel] = useState("default");
  const [settingsValues, setSettingsValues] = useState<Record<string, string>>({});
  const settingsSchema = useBrokerSettingsSchema();
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [zerodhaBusy, setZerodhaBusy] = useState(false);
  const [zerodhaInfo, setZerodhaInfo] = useState<string | null>(null);
  const popupRef = useRef<Window | null>(null);
  // Dedupe: React StrictMode double-fires the callback page's effect, so the
  // same request_token arrives twice. Kite's exchange endpoint is single-use;
  // a second call fails with "Token is invalid or has expired" and overwrites
  // the first call's success state. Skip any token we've already begun handling.
  const processedTokenRef = useRef<string | null>(null);

  // Switching broker resets the credential bag — the fields differ per broker,
  // and carrying e.g. a Zerodha api_secret into a Dhan form would submit junk.
  const selectBroker = useCallback((next: BrokerKind) => {
    setBroker(next);
    setCredentials(
      next === "upstox"
        ? { redirect_uri: `${window.location.origin}/upstox-callback` }
        : {},
    );
    // Settings are broker-specific too, and the server rejects unknown keys —
    // carrying zerodha's market_protection_pct into a Dhan form would 400.
    setSettingsValues({});
    setZerodhaInfo(null);
  }, []);

  // Reconcile the selection with what this user is actually allowed to add.
  //
  // The default was hardcoded to "delta". For a user whose admin enabled only
  // Upstox, `<select value="delta">` matched no <option>, so the browser
  // displayed the first option (Upstox) while state still said delta — the form
  // rendered delta's fields and notes, and with a single option onChange could
  // never fire to fix it. Submitting would have sent Upstox credentials as a
  // delta connection.
  useEffect(() => {
    if (allowed.length === 0) return;
    if (allowed.some((b) => b.broker === broker)) return;
    selectBroker(allowed[0].broker);
    // allowedKey (not `allowed`) — the array identity changes every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedKey, broker, selectBroker]);

  const fields = REQUIRED_CREDENTIAL_FIELDS[broker];

  const create = useMutation({
    mutationFn: createBrokerConnection,
    onSuccess: () => {
      setCredentials({});
      setNotes("");
      setSettingsValues({});
      setLabel("default");
      setError(null);
      setZerodhaInfo(null);
      onSuccess();
    },
    onError: (e) => {
      setError(e instanceof ApiError ? String(e.detail ?? e.message) : "Failed");
    },
  });

  // Listen for the postMessage from the Zerodha callback popup
  useEffect(() => {
    async function handler(event: MessageEvent) {
      if (event.origin !== window.location.origin) return;
      if (
        event.data?.type !== "zerodha-request-token" &&
        event.data?.type !== "zerodha-request-token-error"
      )
        return;
      try {
        popupRef.current?.close();
      } catch {
        // ignore
      }
      popupRef.current = null;
      if (event.data?.type === "zerodha-request-token-error") {
        setZerodhaBusy(false);
        setError(`Zerodha login error: ${event.data.error}`);
        return;
      }
      const requestToken = event.data.request_token as string;
      if (processedTokenRef.current === requestToken) return;
      processedTokenRef.current = requestToken;
      setZerodhaInfo("Exchanging request_token for access_token…");
      try {
        const { access_token } = await exchangeZerodhaToken({
          api_key: (credentials.api_key ?? "").trim(),
          api_secret: (credentials.api_secret ?? "").trim(),
          request_token: requestToken,
        });
        setCredentials({ ...credentials, access_token });
        setZerodhaInfo(
          "✓ access_token populated. Don't forget to set a label and save.",
        );
        setError(null);
      } catch (e) {
        setError(
          e instanceof ApiError
            ? `Exchange failed: ${String(e.detail ?? e.message)}`
            : "Exchange failed (network error)",
        );
        setZerodhaInfo(null);
      } finally {
        setZerodhaBusy(false);
      }
    }
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [credentials]);

  // Listen for the postMessage from the Upstox callback popup
  useEffect(() => {
    async function handler(event: MessageEvent) {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== "upstox-code" && event.data?.type !== "upstox-code-error")
        return;
      try {
        popupRef.current?.close();
      } catch {
        // ignore
      }
      popupRef.current = null;
      if (event.data?.type === "upstox-code-error") {
        setZerodhaBusy(false);
        setError(`Upstox login error: ${event.data.error}`);
        return;
      }
      const code = event.data.code as string;
      if (processedTokenRef.current === code) return;
      processedTokenRef.current = code;
      setZerodhaInfo("Exchanging authorization code for access_token…");
      try {
        const { access_token } = await exchangeUpstoxToken({
          api_key: (credentials.api_key ?? "").trim(),
          api_secret: (credentials.api_secret ?? "").trim(),
          redirect_uri: (credentials.redirect_uri ?? "").trim(),
          code,
        });
        setCredentials({ ...credentials, access_token });
        setZerodhaInfo("✓ access_token populated. Don't forget to set a label and save.");
        setError(null);
      } catch (e) {
        setError(
          e instanceof ApiError
            ? `Exchange failed: ${String(e.detail ?? e.message)}`
            : "Exchange failed (network error)",
        );
        setZerodhaInfo(null);
      } finally {
        setZerodhaBusy(false);
      }
    }
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [credentials]);

  function openUpstoxLogin() {
    setError(null);
    setZerodhaInfo(null);
    if (!credentials.api_key || !credentials.api_secret || !credentials.redirect_uri) {
      setError("Fill api_key, api_secret, and redirect_uri first, then click Open Upstox login.");
      return;
    }
    const url = upstoxLoginUrl(credentials.api_key, credentials.redirect_uri);
    const popup = window.open(url, "upstox-login", "width=600,height=720");
    if (!popup) {
      setError("Popup blocked. Allow popups for this site, then try again.");
      return;
    }
    popupRef.current = popup;
    setZerodhaBusy(true);
    setZerodhaInfo(
      "Browser popup opened. Log into Upstox; we'll capture the code when you're redirected back.",
    );
  }

  function openZerodhaLogin() {
    setError(null);
    setZerodhaInfo(null);
    if (!credentials.api_key || !credentials.api_secret) {
      setError("Fill api_key and api_secret first, then click Generate.");
      return;
    }
    const url = zerodhaLoginUrl(credentials.api_key);
    const popup = window.open(url, "zerodha-login", "width=600,height=720");
    if (!popup) {
      setError("Popup blocked. Allow popups for this site, then click Generate again.");
      return;
    }
    popupRef.current = popup;
    setZerodhaBusy(true);
    setZerodhaInfo(
      "Browser popup opened. Log into Zerodha with your PIN; we'll capture the request_token when you're redirected back.",
    );
  }

  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        const missing = fields.filter((f) => !credentials[f]);
        if (missing.length > 0) {
          setError(`Missing: ${missing.join(", ")}`);
          return;
        }
        create.mutate({
          broker,
          label,
          credentials,
          notes: notes || null,
          settings: toSettingsPayload(settingsValues),
        });
      }}
    >
      {policy && allowed.length === 0 && (
        <div className="text-sm text-warn-700 dark:text-warn-400 bg-warn-50 dark:bg-warn-500/10 border border-warn-200 dark:border-warn-400/30 rounded px-3 py-2">
          No brokers are enabled for your account yet. Ask an admin to enable one
          before adding a connection.
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-fg-subtle mb-1">Broker</label>
          <select
            value={broker}
            onChange={(e) => selectBroker(e.target.value as BrokerKind)}
            className="w-full border border-border rounded px-3 py-2"
          >
            {allowed.map((b) => (
              <option key={b.broker} value={b.broker} disabled={atCap(b.broker)}>
                {BROKER_LABELS[b.broker]} ({b.used} of {b.max_accounts} used)
                {atCap(b.broker) ? " — limit reached" : ""}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-fg-subtle mb-1">Label</label>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className="w-full border border-border rounded px-3 py-2"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {fields.map((f) => (
          <div key={f}>
            <label className="block text-sm font-medium text-fg-subtle mb-1 font-mono">{f}</label>
            <input
              type="password"
              value={credentials[f] ?? ""}
              onChange={(e) => setCredentials({ ...credentials, [f]: e.target.value })}
              className="w-full border border-border rounded px-3 py-2 font-mono text-sm"
              autoComplete="off"
            />
          </div>
        ))}
      </div>

      {broker === "delta" && (
        <div className="bg-warn-50 dark:bg-warn-500/10 border border-warn-200 dark:border-warn-400/30 rounded p-3 text-xs text-warn-700 dark:text-warn-400">
          <div className="font-medium mb-1">Use the India portal, not the international one</div>
          <div>
            Delta Exchange India (
            <a
              href="https://www.delta.exchange/in/app/account/manageapikeys"
              target="_blank"
              rel="noopener noreferrer"
              className="underline font-mono"
            >
              delta.exchange/in
            </a>
            ) is a separate exchange from the international{" "}
            <span className="font-mono">delta.exchange</span> with its own API keys — keys
            from the international site will fail with{" "}
            <span className="font-mono">invalid_api_key</span> here. Also remove any IP
            whitelist on the key, or whitelist the IP this server is running from.
          </div>
        </div>
      )}

      {broker === "zerodha" && (
        <div className="bg-surface-2 border border-border rounded p-3 space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium text-fg-subtle">
              Generate access_token via Kite login
            </div>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={zerodhaBusy}
              onClick={openZerodhaLogin}
            >
              {zerodhaBusy ? "Waiting…" : "Open Kite login"}
            </Button>
          </div>
          <div className="text-xs text-fg-subtle space-y-1">
            <div>
              Fill <span className="font-mono">api_key</span> and{" "}
              <span className="font-mono">api_secret</span> above first, then click
              "Open Kite login". A popup will open kite.zerodha.com, you'll log in with
              your PIN/2FA, and Kite will redirect back to this app — we capture the
              <span className="font-mono"> request_token</span> and exchange it for an
              <span className="font-mono"> access_token</span> automatically.
            </div>
            <div>
              <strong>One-time setup:</strong> your Kite Connect app's <em>redirect URL</em>{" "}
              (in kite.trade) must be set to{" "}
              <code className="font-mono bg-surface px-1 rounded">
                {window.location.origin}/zerodha-callback
              </code>
              .
            </div>
          </div>
          {zerodhaInfo && (
            <div className="text-xs text-up-700 dark:text-up-400 bg-up-50 dark:bg-up-500/10 border border-up-200 dark:border-up-400/30 rounded px-2 py-1">
              {zerodhaInfo}
            </div>
          )}
        </div>
      )}

      {broker === "upstox" && (
        <div className="bg-surface-2 border border-border rounded p-3 space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium text-fg-subtle">
              Generate access_token via Upstox login
            </div>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={zerodhaBusy}
              onClick={openUpstoxLogin}
            >
              {zerodhaBusy ? "Waiting…" : "Open Upstox login"}
            </Button>
          </div>
          <div className="text-xs text-fg-subtle space-y-1">
            <div>
              Fill <span className="font-mono">api_key</span> and{" "}
              <span className="font-mono">api_secret</span> from your app at{" "}
              <a
                href="https://account.upstox.com/developer/apps"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                account.upstox.com
              </a>
              . The <span className="font-mono">redirect_uri</span> is prefilled and must
              EXACTLY match the redirect URL registered on the Upstox app:{" "}
              <code className="font-mono bg-surface px-1 rounded">
                {window.location.origin}/upstox-callback
              </code>
              . Tokens expire ~03:30 IST daily — refresh each morning like Zerodha.
            </div>
          </div>
          {zerodhaInfo && (
            <div className="text-xs text-up-700 dark:text-up-400 bg-up-50 dark:bg-up-500/10 border border-up-200 dark:border-up-400/30 rounded px-2 py-1">
              {zerodhaInfo}
            </div>
          )}
        </div>
      )}

      <BrokerSettingsFields
        specs={settingsSchema.data?.[broker]}
        values={settingsValues}
        onChange={setSettingsValues}
      />

      {/* Rates go stale between releases and nothing inside the app can tell.
          This is how a user checks the shipped card against their own bill. */}
      <ChargesVerifier broker={broker} />

      <div>
        <label className="block text-sm font-medium text-fg-subtle mb-1">Notes (optional)</label>
        <input
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="w-full border border-border rounded px-3 py-2"
        />
      </div>

      {error && (
        <div className="text-sm text-down-700 dark:text-down-400 bg-down-50 dark:bg-down-500/10 border border-down-200 dark:border-down-400/30 rounded px-3 py-2">
          {error}
        </div>
      )}

      <div className="flex gap-2">
        <Button
          type="submit"
          disabled={create.isPending || allowed.length === 0 || atCap(broker)}
        >
          {create.isPending ? "Saving…" : "Add connection"}
        </Button>
      </div>
    </form>
  );
}
